'use client'

import {
  Area,
  AreaChart as RechartsAreaChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
  type ChartData,
} from './chart'

export type ChartAreaConfig = {
  stroke: string
  fill: string
  fillOpacity: number
}

function ChartArea({
  id,
  config,
  areaConfig,
  data,
  hideXAxis = false,
  hideYAxis = false,
  className,
  hideTooltip = false,
  hideGrid = false,
}: {
  id?: string
  config: ChartConfig
  data: ChartData[]
  areaConfig: ChartAreaConfig
  hideXAxis?: boolean
  hideYAxis?: boolean
  hideTooltip?: boolean
  hideGrid?: boolean
  className?: string
}) {
  return (
    <ChartContainer config={config} className={className}>
      <ResponsiveContainer>
        <RechartsAreaChart data={data}>
          {!hideGrid && <CartesianGrid strokeDasharray='3 3' />}
          <XAxis
            dataKey='name'
            axisLine={false}
            tickLine={false}
            hide={hideXAxis}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => value.toLocaleString()}
            hide={hideYAxis}
            domain={[
              () => Math.min(...data.map((d) => d.value)),
              (dataMax: number) => dataMax,
            ]}
          />
          {!hideTooltip && <ChartTooltip content={<ChartTooltipContent />} />}
          <defs>
            <linearGradient id={id || 'colorValue'} x1='0' y1='0' x2='0' y2='1'>
              <stop
                offset='0%'
                stopColor={areaConfig.fill}
                stopOpacity={areaConfig.fillOpacity}
              />
              <stop
                offset='50%'
                stopColor={areaConfig.fill}
                stopOpacity={areaConfig.fillOpacity * 2}
              />
              <stop offset='100%' stopColor={areaConfig.fill} stopOpacity={0} />
            </linearGradient>
          </defs>

          <Area
            type='linear'
            connectNulls
            dot={false}
            layout='horizontal'
            dataKey='value'
            stroke={areaConfig.stroke}
            fill={`url(#${id || 'colorValue'})`}
            fillOpacity={areaConfig.fillOpacity}
          />
        </RechartsAreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}

export { ChartArea }
